import React, { useEffect, useMemo } from 'react';
import { Button } from '@asigna/kit';
import { NoData } from 'components';
import { SendDialog } from 'components/Dialog/TransactionDialog/SendDialog';
import { SkeletonStx20 } from 'components/Skeletons/SkeletonAsset';
import {
  TransactionNotification,
  TransactionResultStatus,
} from 'components/TransactionNotification/TransactionNotification';
import { STX_DECIMALS } from 'constants/base';
import useNetwork from 'hooks/use-network';
import { useFloorPrices } from 'hooks/useFloorPrices';
import StxIcon from 'images/stacks-stx-logo.svg';
import { uniqueId } from 'lodash';
import BoxIcon from 'modules/icons/icon-box.svg';

import { displayFullAmount, getBalanceAddress } from '../../../utils';

import { useTokenTab } from './util/useTokenTab';
import { useTokenStyles } from './TokenStyles';

type Props = {
  currentSafe: string;
};

export const TokenTab = ({ currentSafe }: Props) => {
  const classes = useTokenStyles();
  const { getFloorPrice, loadFloorPrice } = useFloorPrices();
  const [network] = useNetwork();
  const {
    isLoading,
    setLoading,
    searchAddressResult,
    setSearchAddressResult,
    selectedTokenIndex,
    setSelectedTokenIndex,
    instantSendDialogShown,
    setInstantSendDialogShown,
    transactionMessagePopupShown,
    setTransactionMessagePopupShown,
    transactionResultStatus,
    setTransactionResultStatus,
    transactionResultMessage,
    setTransactionResultMessage,
  } = useTokenTab();

  const fetchAddress = async (address: string) => {
    setLoading(true);
    setSearchAddressResult(undefined);
    try {
      const result = await getBalanceAddress(address, network);
      setSearchAddressResult(result);
    } catch (error) {
      // console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddress(currentSafe);
  }, [currentSafe]);

  useEffect(() => {
    if (searchAddressResult?.balances.length === 0) return;
    searchAddressResult?.balances.map((balance) => loadFloorPrice(balance.ticker));
  }, [searchAddressResult?.balances]);

  const tokenList = useMemo(() => {
    if (searchAddressResult?.balances.length === 0) return [];

    return searchAddressResult?.balances.map((balance) => {
      return {
        symbol: balance.ticker,
        incomingTotalBalance: balance.balance,
        address: '',
        balance: balance.balance,
        totalBalance: balance.balance,
        isLess: false,
        decimals: 0,
        tokenName: balance.ticker,
        incomingBalance: balance.balance,
        outGoingTotalBalance: balance.balance,
        tokenIcon: StxIcon,
        outGoingBalance: balance.balance,
        ref: '',
        isSTX20: true,
      };
    });
  }, [searchAddressResult?.balances]);

  // useEffect(() => console.log('tokenList', tokenList), [tokenList]);

  return (
    <div className={classes.tokenWrapper}>
      {searchAddressResult?.balances && searchAddressResult?.balances.length > 0 && (
        <div className={classes.tokenHeader}>
          <span>Token</span>
          {/* <span>Registered At</span> */}
          <span>Amount</span>
          <span></span>
        </div>
      )}
      {isLoading ? (
        <SkeletonStx20 />
      ) : (
        <div className={classes.contentWrapper}>
          {searchAddressResult?.balances && searchAddressResult?.balances.length > 0 ? (
            searchAddressResult?.balances.map((balance, index) => {
              const floorPrice = getFloorPrice(balance.ticker) / Math.pow(10, STX_DECIMALS);
              const priceUsd = floorPrice * Number(balance.balance);

              return (
                <div className={classes.tableRow} key={uniqueId(balance.ticker)}>
                  <div className={classes.token}>
                    <div className={classes.rowTableIcon}>
                      <img src={BoxIcon} alt="" />
                    </div>
                    <div className={classes.title}>{balance.ticker}</div>
                  </div>
                  <div className={classes.itemColumn}>
                    <div className={classes.title}>
                      {displayFullAmount(Number(balance.balance), 4)}
                    </div>
                    <div className={classes.value}>${displayFullAmount(priceUsd, 2)}</div>
                  </div>
                  <div className={classes.itemActions}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTokenIndex(index);
                        setInstantSendDialogShown(true);
                      }}
                      message="Send"
                      colorType="secondary"
                    />
                    <Button
                      colorType="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://stx20.com/marketplace', `_blank`);
                      }}
                      message="Sell"
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className={classes.noDataWrapper}>
              <NoData isAssets style={{ margin: '32px 0 16px' }} />
              <Button
                onClick={() => window.open('https://stx20.com/marketplace', `_blank`)}
                message="Go to STX20 Marketplace"
                colorType="secondary"
                size="md"
              />
            </div>
          )}
        </div>
      )}
      {instantSendDialogShown && (
        <SendDialog
          open={instantSendDialogShown}
          tokenList={tokenList || []}
          safeAddress={currentSafe}
          selectedTokenIndex={selectedTokenIndex}
          onClose={() => setInstantSendDialogShown(false)}
          onContinue={(successful, message) => {
            setTransactionMessagePopupShown(true);
            setTransactionResultStatus(
              successful ? TransactionResultStatus.success : TransactionResultStatus.rejected
            );
            setTransactionResultMessage(message);
          }}
        />
      )}
      {transactionMessagePopupShown && (
        <TransactionNotification
          open={transactionMessagePopupShown}
          onClose={() => setTransactionMessagePopupShown(false)}
          resultStatus={transactionResultStatus}
          description={transactionResultMessage}
        />
      )}
    </div>
  );
};
